import React from 'react';
const { useState, useEffect } = React;
import { coachCall } from '../coaching/shared.jsx';

// «Por atender» of Acompañamiento in Hoy (only when Ale is also the coach): reservations to schedule with «Ya la
// agendé», new messages and submissions to review, each once and with its shortcut. The coach's own data, read
// from coaching-admin (the coach only); Mi espacio's server never reads it and nothing is saved in Mi espacio.
// Hoy lists these items inside «Pendientes» (useCoachAttention + AttentionRow); the card is kept for other uses.
// Without coach access (403) nothing is shown; any other failure is shown with «Reintentar», never as «al día».
// Oike never writes her calendar: «Ya la agendé» only records that she did it (for that exact time).

const cardSt = { border: '1px solid var(--globant-border)', borderRadius: 12, padding: 14, display: 'grid', gap: 10, background: 'var(--globant-card, transparent)' };
const labelSt = { fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--globant-muted)' };
const mutedSt = { fontSize: 12, color: 'var(--globant-muted)' };
const rowSt = { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '6px 0', fontSize: 13 };
const btn = { fontSize: 11, padding: '3px 10px' };
const when = (iso, tz) => { try { return new Date(iso).toLocaleString('es', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', ...(tz ? { timeZone: tz } : {}) }); } catch { return iso; } };
const openCoaching = (detail) => window.dispatchEvent(new CustomEvent('oike:navigate', { detail: { page: 'coaching', ...detail } }));

/** «Por atender», loaded from coaching-admin: status loading | ok | not_coach | error (with its message). */
export function useCoachAttention() {
  const [state, setState] = useState({ status: 'loading', data: null, error: '' });
  const reload = () => {
    setState(s => ({ ...s, status: s.data ? s.status : 'loading', error: '' }));
    return coachCall({ action: 'attention' })
      .then(data => setState({ status: 'ok', data, error: '' }))
      .catch(e => setState(e.status === 403 || e.status === 401 || e.code === 'forbidden'
        ? { status: 'not_coach', data: null, error: '' }
        : { status: 'error', data: null, error: e.message || 'No se pudo leer Acompañamiento.' }));
  };
  useEffect(() => { reload(); }, []);
  return { ...state, reload };
}

/** One item of «Por atender»: a reservation to schedule, a new message or a submission to review. */
export function AttentionRow({ type, item, timeZone, onChanged, onMessage }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  if (type === 'booking') {
    const scheduled = async () => {
      setBusy(true); setMsg('');
      try { await coachCall({ action: 'markCalendarAdded', sessionId: item.sessionId }); await onChanged?.(); }
      catch (e) { setMsg(e.message); setBusy(false); }
    };
    return (
      <div style={rowSt}>
        <span aria-hidden="true">📅</span>
        <span style={{ flex: '1 1 220px', minWidth: 0 }}><b>Agendar la reserva de {item.personName || 'una persona'}</b>{item.number ? ` · sesión ${item.number}` : ''}<span style={mutedSt}> · {when(item.startsAt, timeZone)} · {item.processName}{item.rescheduled ? ' · cambió de horario' : ''}</span></span>
        <button type="button" className="action-btn btn-primary" style={btn} disabled={busy} onClick={scheduled}>{busy ? 'Guardando…' : 'Ya la agendé'}</button>
        <button type="button" className="action-btn" style={btn} onClick={() => openCoaching({ processId: item.processId, at: { tab: 'sessions', session: item.sessionId } })}>Abrir</button>
        {msg && <span role="alert" style={{ ...mutedSt, color: '#9A6212', flexBasis: '100%' }}>{msg}</span>}
      </div>
    );
  }
  if (type === 'message') {
    const open = async () => {
      try { await coachCall({ action: 'markNotificationsRead', ids: [item.id] }); } catch { /* opening still works */ }
      onMessage?.();
      openCoaching({ processId: item.processId, at: { tab: 'messages', thread: item.channel === 'group' ? 'group' : item.threadMemberId } });
    };
    return (
      <div style={rowSt}>
        <span aria-hidden="true">💬</span>
        <span style={{ flex: '1 1 220px', minWidth: 0 }}>{item.text}{item.count > 1 ? ` (${item.count})` : ''}<span style={mutedSt}> · {item.processName}</span></span>
        <button type="button" className="action-btn" style={btn} onClick={open}>Abrir conversación</button>
      </div>
    );
  }
  return (
    <div style={rowSt}>
      <span aria-hidden="true">📝</span>
      <span style={{ flex: '1 1 220px', minWidth: 0 }}><b>Revisar la entrega de {item.personName}</b> · {item.lessonTitle}<span style={mutedSt}> · {item.courseName}{item.attempt > 1 ? ` · versión ${item.attempt}` : ''}</span></span>
      <button type="button" className="action-btn" style={btn} onClick={() => openCoaching({ section: 'learning', at: { enrollmentId: item.enrollmentId, lessonId: item.lessonId } })}>Revisar</button>
    </div>
  );
}

/** The card on its own (not used in Hoy, which lists the items in «Pendientes»). */
export default function CoachAttention({ timeZone }) {
  const a = useCoachAttention();
  if (a.status === 'not_coach' || a.status === 'loading') return null;
  if (a.status === 'error') return <section aria-label="Por atender en Acompañamiento" style={cardSt}><span style={labelSt}>Por atender · Acompañamiento</span><div role="alert" style={{ fontSize: 12, color: '#9A6212' }}>{a.error} <button type="button" className="action-btn" style={btn} onClick={a.reload}>Reintentar</button></div></section>;
  const d = a.data;
  const rows = [...d.toSchedule.map(x => ['booking', x, x.sessionId]), ...d.messages.map(x => ['message', x, x.id]), ...d.reviews.map(x => ['review', x, x.submissionId])];
  return (
    <section aria-label="Por atender en Acompañamiento" style={cardSt}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <span style={labelSt}>Por atender · Acompañamiento</span>
        <span style={mutedSt}>{rows.length ? `${rows.length} pendiente${rows.length === 1 ? '' : 's'}` : 'Al día'}</span>
      </div>
      {rows.map(([type, item, id]) => <AttentionRow key={`${type}:${id}`} type={type} item={item} timeZone={timeZone} onChanged={a.reload} />)}
    </section>
  );
}
